/**
 * ReportModal Component
 * 4-step wizard for reporting users, communities, or rituals:
 *   1. Select reason
 *   2. Additional details (optional)
 *   3. Review & submit
 *   4. Success confirmation
 */

import { ArrowLeft, Flag, Pencil } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Avatar } from "@/components/ui/avatar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useReportCommunity } from "@/hooks/useReportCommunity"
import { useReportRitual } from "@/hooks/useReportRitual"
import { useReportUser } from "@/hooks/useReportUser"
import { useTranslation } from "@/language"
import type {
  ReportCommunityReason,
  ReportReason,
  ReportRitualReason,
} from "@/services/report.service"

export type ReportTarget = "USER" | "COMMUNITY" | "RITUAL"

const USER_REASONS: ReportReason[] = [
  "HARASSMENT",
  "SPAM",
  "INAPPROPRIATE_CONTENT",
  "HATE_SPEECH",
  "IMPERSONATION",
  "OTHER",
]

const COMMUNITY_REASONS: ReportCommunityReason[] = [
  "HARMFUL_ENVIRONMENT",
  "INAPPROPRIATE_CONTENT",
  "SPAM",
  "HATE_SPEECH",
  "MISLEADING",
  "OTHER",
]

const RITUAL_REASONS: ReportRitualReason[] = [
  "HARMFUL_PRACTICES",
  "MISLEADING",
  "INAPPROPRIATE_CONTENT",
  "SPAM",
  "CULTURAL_INSENSITIVITY",
  "OTHER",
]

function ReasonDisplayCard({
  reason,
  onEdit,
  t,
  translationPrefix,
}: {
  reason: string
  onEdit?: () => void
  t: (key: string) => string
  translationPrefix: string
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="w-full flex items-center justify-between rounded-[15px] border border-dashed border-white/10 px-5 py-4 text-left"
    >
      <div>
        <p className="text-[14px] font-semibold text-primary/90">
          {t(`${translationPrefix}.reasons.${reason}.title`)}
        </p>
        <p className="text-[14px] text-body">
          {t(`${translationPrefix}.reasons.${reason}.description`)}
        </p>
      </div>
      {onEdit && <Pencil className="w-5 h-5 text-primary/50 shrink-0" />}
    </button>
  )
}

export interface ReportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: ReportTarget
  targetData: {
    id: string
    name: string
    handle?: string
    imageUrl?: string | null
  }
  /** When set, the report targets a specific comment. The comment's author becomes the reported user. */
  commentId?: string
}

// Separate component for each target to ensure hooks are called unconditionally
function ReportUserModalContent({
  targetData,
  open,
  onOpenChange,
  commentId,
}: Omit<ReportModalProps, "target">) {
  const mutation = useReportUser()
  return (
    <ReportModalContent
      target="USER"
      targetData={targetData}
      mutation={mutation}
      open={open}
      onOpenChange={onOpenChange}
      commentId={commentId}
    />
  )
}

function ReportCommunityModalContent({
  targetData,
  open,
  onOpenChange,
}: Omit<ReportModalProps, "target">) {
  const mutation = useReportCommunity()
  return (
    <ReportModalContent
      target="COMMUNITY"
      targetData={targetData}
      mutation={mutation}
      open={open}
      onOpenChange={onOpenChange}
    />
  )
}

function ReportRitualModalContent({
  targetData,
  open,
  onOpenChange,
}: Omit<ReportModalProps, "target">) {
  const mutation = useReportRitual()
  return (
    <ReportModalContent
      target="RITUAL"
      targetData={targetData}
      mutation={mutation}
      open={open}
      onOpenChange={onOpenChange}
    />
  )
}

// Shared content component that uses the injected mutation
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ReportModalContent({
  target,
  targetData,
  open,
  onOpenChange,
  mutation,
  commentId,
}: {
  target: ReportTarget
  targetData: ReportModalProps["targetData"]
  open: boolean
  onOpenChange: (open: boolean) => void
  mutation: unknown
  commentId?: string
}) {
  const { t } = useTranslation("common")

  // biome-ignore lint/suspicious/noExplicitAny: Different mutation types require any cast
  const isPending = (mutation as any).isPending as boolean

  // Memoize reasons and translation prefix to prevent recalculation
  const reasons = useMemo(
    () =>
      target === "USER"
        ? USER_REASONS
        : target === "COMMUNITY"
          ? COMMUNITY_REASONS
          : RITUAL_REASONS,
    [target],
  )

  const translationPrefix = useMemo(
    () =>
      target === "USER" ? "report" : target === "COMMUNITY" ? "reportCommunity" : "reportRitual",
    [target],
  )

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [selectedReason, setSelectedReason] = useState<string | null>(null)
  const [details, setDetails] = useState("")
  const [submitError, setSubmitError] = useState<string | null>(null)
  const prevOpenRef = useRef(open)

  // Reset state when dialog opens (not when closing) to avoid infinite loop
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setStep(1)
      setSelectedReason(null)
      setDetails("")
      setSubmitError(null)
      // Skip mutation.reset() - it might cause issues
    }
    prevOpenRef.current = open
  }, [open])

  async function handleSubmit(): Promise<void> {
    if (!selectedReason) return
    setSubmitError(null)

    try {
      if (target === "USER") {
        // biome-ignore lint/suspicious/noExplicitAny: Different mutation types require any cast
        await (mutation as any).mutateAsync({
          reportedUserId: targetData.id,
          reason: selectedReason as ReportReason,
          details: details.trim() || undefined,
          ...(commentId ? { commentId } : {}),
        })
      } else if (target === "COMMUNITY") {
        // biome-ignore lint/suspicious/noExplicitAny: Different mutation types require any cast
        await (mutation as any).mutateAsync({
          communityId: targetData.id,
          reason: selectedReason as ReportCommunityReason,
          details: details.trim() || undefined,
        })
      } else {
        // biome-ignore lint/suspicious/noExplicitAny: Different mutation types require any cast
        await (mutation as any).mutateAsync({
          ritualId: targetData.id,
          reason: selectedReason as ReportRitualReason,
          details: details.trim() || undefined,
        })
      }
      setStep(4)
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as Error)?.message ||
        "Failed to submit report"
      setSubmitError(message)
    }
  }

  // Shared target info card
  const targetCard = (
    <div className="flex items-center gap-3 rounded-[15px] border border-dashed border-white/10 px-5 py-4">
      <Avatar
        src={targetData.imageUrl ?? undefined}
        fallback={targetData.name || "?"}
        size="md"
        className="w-10 h-10"
      />
      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        <span className="text-[14px] leading-[20px] font-semibold text-primary">
          {targetData.name}
        </span>
        {targetData.handle && (
          <span className="text-[14px] leading-[20px] text-primary/50">@{targetData.handle}</span>
        )}
      </div>
      <Flag className="w-5 h-5 fill-brand-terracotta text-brand-terracotta shrink-0" />
    </div>
  )

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isPending) return
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`overflow-scroll px-5 py-[50px] bg-[#020202] border-none rounded-none w-full max-w-full  md:max-h-[calc(100vh-100px)] md:max-w-[700px] md:top-[50%] md:left-[50%] md:translate-x-[-50%] md:translate-y-[-50%] md:rounded-tr-[30px] md:rounded-bl-[30px] md:bg-white/5 md:py-3.5 ${step === 4 ? "flex items-center justify-center h-full md:max-h-[299px]" : "items-start justify-start md:items-center md:justify-center top-0 left-0 translate-x-0 translate-y-0"}`}
        overlayClass="bg-[rgba(2,2,2,0.9)] backdrop-blur-[25px]"
        onPointerDownOutside={e => {
          e.preventDefault()
        }}
        onEscapeKeyDown={e => {
          if (isPending) e.preventDefault()
        }}
      >
        {/* Steps 1-3: Header */}
        {step !== 4 && (
          <DialogHeader className="gap-1 items-start">
            <DialogTitle className="text-lg font-semibold text-[#F2F0EB]/90">
              {t(`${translationPrefix}.title`)}
            </DialogTitle>
            <DialogDescription className="text-sm text-white">
              {t(`${translationPrefix}.subtitle`)}
            </DialogDescription>
          </DialogHeader>
        )}

        {/* Step 1: Select Reason */}
        {step === 1 && (
          <div className="space-y-3 mt-1">
            {targetCard}

            <p className="text-sm font-semibold text-white pt-2.5">
              {t(`${translationPrefix}.selectReason`)}
            </p>

            <div className="mt-5.5 space-y-3 ">
              {reasons.map(reason => {
                const isSelected = selectedReason === reason
                return (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => setSelectedReason(reason)}
                    className={`w-full text-left rounded-[15px] border px-4.5 py-4 transition-colors ${
                      isSelected
                        ? "bg-[rgba(200,103,87,0.1)] border-brand-terracotta"
                        : "bg-white/5 border-white/10 hover:border-white/20"
                    }`}
                  >
                    <p className="text-[14px] leading-[20px] font-semibold text-[#F2F0EB]/90">
                      {t(`${translationPrefix}.reasons.${reason}.title`)}
                    </p>
                    <p className="text-[14px] leading-[20px] text-[#F2F0EB]/50">
                      {t(`${translationPrefix}.reasons.${reason}.description`)}
                    </p>
                  </button>
                )
              })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4">
              <div />
              <button
                type="button"
                disabled={!selectedReason}
                onClick={() => setStep(2)}
                className="cursor-pointer h-[50px] w-[158px] rounded-[100px] bg-[#F2F0EB] text-[#020202] text-sm font-semibold uppercase hover:bg-[#E8E6E1] disabled:opacity-20 transition-colors"
              >
                {t(`${translationPrefix}.next`)}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Additional Details */}
        {step === 2 && (
          <div className="space-y-3 mt-1">
            {targetCard}
            {selectedReason && (
              <ReasonDisplayCard
                reason={selectedReason}
                onEdit={() => setStep(1)}
                t={t}
                translationPrefix={translationPrefix}
              />
            )}

            <div className="pt-2">
              <p className="text-sm font-semibold text-white">
                {t(`${translationPrefix}.additionalDetails`)}
              </p>
              <p className="text-sm text-white/50 mt-2">
                {t(`${translationPrefix}.additionalDetailsHint`)}
              </p>
            </div>

            <textarea
              value={details}
              onChange={e => setDetails(e.target.value.slice(0, 2000))}
              placeholder={t(`${translationPrefix}.detailsPlaceholder`)}
              className="w-full h-[150px] rounded-[15px] mt-3 border border-white/15 bg-transparent px-5 py-4 text-sm text-primary placeholder:text-primary/50 resize-none focus:outline-none focus:ring-1 focus:ring-white/20"
            />

            {/* Footer */}
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="cursor-pointer flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                {t(`${translationPrefix}.back`)}
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="cursor-pointer h-[50px] w-[158px] rounded-[100px] bg-[#F2F0EB] text-[#020202] text-sm font-semibold uppercase hover:bg-[#E8E6E1] disabled:opacity-20 transition-colors"
              >
                {t(`${translationPrefix}.next`)}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Submit */}
        {step === 3 && (
          <div className="space-y-3 mt-4">
            <p className="text-sm font-semibold text-white">
              {t(`${translationPrefix}.reviewTitle`)}
            </p>

            <div className="mt-5">{targetCard}</div>

            {selectedReason && (
              <ReasonDisplayCard
                reason={selectedReason}
                onEdit={() => setStep(1)}
                t={t}
                translationPrefix={translationPrefix}
              />
            )}

            {/* Details review card */}
            <button
              type="button"
              onClick={() => setStep(2)}
              className="w-full text-left rounded-[15px] border border-dashed border-white/10 px-5 py-4 mt-0"
            >
              <div className="flex justify-between items-start min-h-[150px]">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] leading-[20px] text-primary/90">
                    {t(`${translationPrefix}.additionalDetails`)}
                  </p>
                  {details.trim() && (
                    <p className="text-[14px] leading-[20px] font-semibold text-primary/90 mt-1">
                      {details}
                    </p>
                  )}
                </div>
                <Pencil className="w-5 h-5 text-primary/50 shrink-0 mt-0.5 ml-3" />
              </div>
            </button>

            {/* Disclaimer */}
            <p className="text-sm mt-3 text-brand-terracotta leading-[22px]">
              {t(`${translationPrefix}.disclaimer`)}
            </p>

            {/* Inline error */}
            {submitError && (
              <p className="text-sm text-red-400 bg-red-400/10 rounded-[10px] px-4 py-2.5">
                {submitError}
              </p>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="cursor-pointer flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                {t(`${translationPrefix}.back`)}
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={handleSubmit}
                className="cursor-pointer h-[50px] w-[158px] rounded-[100px] bg-brand-terracotta text-[#F2F0EB] text-sm font-semibold uppercase hover:bg-brand-terracotta-hover disabled:opacity-20 transition-colors"
              >
                {isPending
                  ? t(`${translationPrefix}.submitting`)
                  : t(`${translationPrefix}.report`)}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="flex flex-col items-center text-center py-[45px]">
            <DialogTitle className="sr-only capitalize">
              {t(`${translationPrefix}.successTitle`)}
            </DialogTitle>
            <h2 className="text-2xl font-semibold text-[#F2F0EB]/90 capitalize">
              {t(`${translationPrefix}.successTitle`)}
            </h2>
            <p className="text-sm text-primary mt-4 whitespace-pre-line leading-[25px]">
              {t(`${translationPrefix}.successBody`)}
            </p>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="cursor-pointer mt-8 h-[50px] w-[280px] rounded-[100px] bg-[#F2F0EB] text-[#020202] text-sm font-semibold uppercase hover:bg-[#E8E6E1] disabled:opacity-20 transition-colors"
            >
              {t(`${translationPrefix}.done`)}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// Main export with key to force remount when target changes
export function ReportModal(props: ReportModalProps) {
  const { target, targetData, open, onOpenChange, commentId } = props
  // Each component calls its own mutation hook unconditionally
  if (target === "USER") {
    return (
      <ReportUserModalContent
        targetData={targetData}
        open={open}
        onOpenChange={onOpenChange}
        commentId={commentId}
      />
    )
  }
  if (target === "COMMUNITY") {
    return (
      <ReportCommunityModalContent
        targetData={targetData}
        open={open}
        onOpenChange={onOpenChange}
      />
    )
  }
  return (
    <ReportRitualModalContent targetData={targetData} open={open} onOpenChange={onOpenChange} />
  )
}
